class Slideshow {
  constructor(root) {
    this.root = root
    this.track = root.querySelector(".c-slideshow__track")
    this.originalSlides = Array.from(root.querySelectorAll(".c-slideshow__slide"))
    this.dots = Array.from(root.querySelectorAll(".c-slideshow__dot"))
    this.slideById = new Map(this.originalSlides.map((slide, index) => [slide.id, index]))
    this.originalCount = this.originalSlides.length
    this.slides = []
    this.cloneCount = 0
    this.activeRealIndex = 0
    this.activeVirtualIndex = 0
    this.frame = null
    this.suspendClosestTrackingUntil = 0
    this.normalizeTimer = null
    this.resizeTimer = null
    this.cachedSlideWidth = 0
    this.cachedGap = 0
    this.reducedMotionMQ = window.matchMedia("(prefers-reduced-motion: reduce)")

    if (!this.track || !this.originalCount) {
      return
    }

    this.prepareLoop()
    this.bind()
    this.init()
  }

  bind() {
    this.onScroll = this.onScroll.bind(this)
    this.onResize = this.onResize.bind(this)
    this.onTrackClick = this.onTrackClick.bind(this)
    this.onTrackKeydown = this.onTrackKeydown.bind(this)
    this.onDotClick = this.onDotClick.bind(this)

    this.track.addEventListener("scroll", this.onScroll, { passive: true })
    this.track.addEventListener("click", this.onTrackClick)
    this.track.addEventListener("keydown", this.onTrackKeydown)
    window.addEventListener("resize", this.onResize)

    for (const dot of this.dots) {
      dot.addEventListener("click", this.onDotClick)
    }
  }

  init() {
    const startIndex = this.getStartIndex()
    this.scrollToVirtualIndex(this.cloneCount + startIndex, false)
    this.setActiveByVirtualIndex(this.findClosestVirtualIndex())
  }

  getStartIndex() {
    const raw = Number.parseInt(this.root.dataset.startIndex || "0", 10)
    if (Number.isNaN(raw)) return 0
    return Math.max(0, Math.min(this.originalCount - 1, raw))
  }

  onScroll() {
    if (this.frame) return
    this.frame = window.requestAnimationFrame(() => {
      this.frame = null
      const isSuspended = Date.now() < this.suspendClosestTrackingUntil
      // Skip normalization while a programmatic smooth scroll is in progress.
      // The normalizeTimer will handle it once the scroll settles.
      if (!isSuspended) {
        this.normalizeLoopPosition()
        this.setActiveByVirtualIndex(this.findClosestVirtualIndex())
      }
    })
  }

  onResize() {
    clearTimeout(this.resizeTimer)
    this.resizeTimer = setTimeout(() => {
      this.prepareLoop()
      const startVirtualIndex = this.cloneCount + this.activeRealIndex
      this.scrollToVirtualIndex(startVirtualIndex, false)
      this.setActiveByVirtualIndex(this.findClosestVirtualIndex())
    }, 150)
  }

  onTrackKeydown(event) {
    if (event.key === "ArrowLeft") {
      event.preventDefault()
      this.scrollToVirtualIndex(this.activeVirtualIndex - 1, true)
    } else if (event.key === "ArrowRight") {
      event.preventDefault()
      this.scrollToVirtualIndex(this.activeVirtualIndex + 1, true)
    }
  }

  onTrackClick(event) {
    const control = event.target.closest(".c-slideshow__control")
    if (!control) return

    event.preventDefault()
    const direction = control.classList.contains("c-slideshow__control--prev") ? -1 : 1
    // Scroll directly into the clone zone rather than wrapping backwards.
    // The normalizeTimer in scrollToVirtualIndex will do the seamless reset
    // after the animation finishes.
    this.scrollToVirtualIndex(this.activeVirtualIndex + direction, true)
  }

  onDotClick(event) {
    const href = event.currentTarget.getAttribute("href") || ""
    if (!href.startsWith("#")) return

    const targetRealIndex = this.slideById.get(href.slice(1))
    if (typeof targetRealIndex !== "number") return

    event.preventDefault()
    this.scrollToRealIndex(targetRealIndex, true)
  }

  // O(1): all slides share the same width and gap in a CSS grid,
  // so the closest index is direct arithmetic on scrollLeft.
  findClosestVirtualIndex() {
    const step = this.cachedSlideWidth + this.cachedGap
    if (!step) return this.cloneCount
    const scrollCenter = this.track.scrollLeft + this.track.clientWidth / 2
    const idx = Math.round((scrollCenter - this.cachedSlideWidth / 2) / step)
    return this.clamp(idx, 0, this.slides.length - 1)
  }

  scrollToVirtualIndex(index, smooth) {
    const slide = this.slides[index]
    if (!slide) return

    const step = this.cachedSlideWidth + this.cachedGap
    const targetScrollLeft = this.clamp(
      index * step - (this.track.clientWidth - this.cachedSlideWidth) / 2,
      0,
      this.track.scrollWidth - this.track.clientWidth
    )

    const shouldAnimate = smooth && !this.reducedMotionMQ.matches

    this.track.scrollTo({
      left: targetScrollLeft,
      behavior: shouldAnimate ? "smooth" : "auto"
    })

    const delay = shouldAnimate ? 650 : 100
    this.suspendClosestTrackingUntil = Date.now() + delay
    this.setActiveByVirtualIndex(index)

    // After an animated scroll settles, normalize if we landed on a clone.
    if (shouldAnimate) {
      clearTimeout(this.normalizeTimer)
      this.normalizeTimer = setTimeout(() => {
        this.normalizeLoopPosition()
      }, delay + 50)
    }
  }

  scrollToRealIndex(realIndex, smooth, direction = "next") {
    const currentVirtual = this.findClosestVirtualIndex()
    const candidates = [
      realIndex + this.cloneCount - this.originalCount,
      realIndex + this.cloneCount,
      realIndex + this.cloneCount + this.originalCount
    ]

    const directionalCandidates = candidates.filter((candidate) => {
      if (direction === "prev") return candidate < currentVirtual
      return candidate > currentVirtual
    })

    const pool = directionalCandidates.length ? directionalCandidates : candidates
    let targetVirtual = pool[0]
    let minDistance = Math.abs(pool[0] - currentVirtual)

    for (const candidate of pool) {
      const distance = Math.abs(candidate - currentVirtual)
      if (distance < minDistance) {
        minDistance = distance
        targetVirtual = candidate
      }
    }

    this.scrollToVirtualIndex(targetVirtual, smooth)
  }

  setActiveByVirtualIndex(virtualIndex) {
    const slide = this.slides[virtualIndex]
    if (!slide) return
    const realIndex = Number.parseInt(slide.dataset.realIndex || "0", 10)

    this.activeVirtualIndex = virtualIndex
    this.activeRealIndex = realIndex
    this.root.classList.add("is-ready")
    this.root.classList.remove("is-initial")

    this.slides.forEach((item, idx) => {
      const offset = idx - virtualIndex
      const absOffset = Math.abs(offset)

      item.classList.toggle("is-active", idx === virtualIndex)
      item.classList.toggle("is-fade-left-1", offset === -1)
      item.classList.toggle("is-fade-left-2", offset === -2)
      item.classList.toggle("is-fade-left-3", offset === -3)
      item.classList.toggle("is-fade-right-1", offset === 1)
      item.classList.toggle("is-fade-right-2", offset === 2)
      item.classList.toggle("is-fade-right-3", offset === 3)
      item.classList.toggle("is-fade-out", absOffset > 3)
      item.classList.remove("is-initial")
    })

    this.dots.forEach((dot, idx) => {
      const isActive = idx === realIndex
      dot.classList.toggle("is-active", isActive)
      dot.classList.remove("is-initial")
      dot.setAttribute("aria-current", isActive ? "true" : "false")
    })
  }

  normalizeLoopPosition() {
    const closest = this.findClosestVirtualIndex()
    const minOriginal = this.cloneCount
    const maxOriginal = this.cloneCount + this.originalCount - 1

    if (closest < minOriginal) {
      this.shiftScrollByCycle(closest, closest + this.originalCount)
      return
    }

    if (closest > maxOriginal) {
      this.shiftScrollByCycle(closest, closest - this.originalCount)
    }
  }

  shiftScrollByCycle(fromIndex, toIndex) {
    const step = this.cachedSlideWidth + this.cachedGap
    const delta = (toIndex - fromIndex) * step
    if (!delta) return

    const previousBehavior = this.track.style.scrollBehavior
    this.root.classList.add("is-loop-shifting")
    this.track.style.scrollBehavior = "auto"
    this.track.scrollLeft += delta
    this.track.style.scrollBehavior = previousBehavior

    window.requestAnimationFrame(() => {
      this.root.classList.remove("is-loop-shifting")
    })
  }

  prepareLoop() {
    this.track.querySelectorAll(".c-slideshow__slide.is-clone").forEach((clone) => clone.remove())
    this.originalSlides = Array.from(this.track.querySelectorAll(".c-slideshow__slide:not(.is-clone)"))
    this.originalCount = this.originalSlides.length

    this.originalSlides.forEach((slide, index) => {
      slide.dataset.realIndex = String(index)
    })

    this.cloneCount = this.getCloneCount()

    const prepend = this.originalSlides.slice(-this.cloneCount).map((slide) => this.createClone(slide))
    const append = this.originalSlides.slice(0, this.cloneCount).map((slide) => this.createClone(slide))

    for (const clone of prepend.reverse()) {
      this.track.prepend(clone)
    }

    for (const clone of append) {
      this.track.append(clone)
    }

    this.slides = Array.from(this.track.querySelectorAll(".c-slideshow__slide"))
    this.cacheLayout()
  }

  cacheLayout() {
    this.cachedGap = parseFloat(getComputedStyle(this.track).columnGap) || 0
    this.cachedSlideWidth = this.originalSlides[0]?.getBoundingClientRect().width || 0
  }

  createClone(slide) {
    const clone = slide.cloneNode(true)
    clone.classList.add("is-clone")
    clone.setAttribute("aria-hidden", "true")
    clone.removeAttribute("id")
    return clone
  }

  getCloneCount() {
    const firstSlide = this.originalSlides[0]
    if (!firstSlide) return 1

    const slideWidth = firstSlide.getBoundingClientRect().width
    if (!slideWidth) return 1

    const visibleCount = Math.max(1, Math.round(this.track.clientWidth / slideWidth))
    return Math.max(3, visibleCount)
  }

  clamp(value, min, max) {
    return Math.min(max, Math.max(min, value))
  }
}

const slideshows = document.querySelectorAll(".c-slideshow")
for (const root of slideshows) {
  new Slideshow(root)
}

export default Slideshow
